import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";
import { useOtherOrder, useOtherOrderStore } from "hooks";
import { OtherOrder, OtherOrderItem, OtherOrderQuery } from "types";
import { ROUTES } from "utils";
import SnackbarUtils from "utils/SnackbarUtils";
import {
  compareDateFilter,
  formatDateSafe,
} from "utils/helpers/accounting";
import { parseNumber } from "utils/helpers/data_helpers";
import { sanitizeOtherOrderItems } from "utils/helpers/sanitizers";

const sanitizeItems = (items?: OtherOrderItem[]): OtherOrderItem[] =>
  sanitizeOtherOrderItems(items);

const parseItems = (value: unknown): OtherOrderItem[] => {
  if (Array.isArray(value)) {
    return sanitizeItems(value);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return sanitizeItems(parsed);
      }
    } catch {
      // ignore parsing errors
    }
  }

  return [];
};

const withItemFields = (order: OtherOrder): OtherOrder => ({
  ...order,
  items: sanitizeItems(order.items),
});

const OtherOrders: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<OtherOrderQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<OtherOrder | null>(null);
  const [selectedItems, setSelectedItems] = useState<OtherOrderItem[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");

  const { rows, setRows, removeRow, isLoading, setLoading } =
    useOtherOrderStore();

  const { fetchList, deleteOtherOrder } = useOtherOrder();
  const { data: otherOrderData, isFetching } = fetchList(queryParams, true);

  useEffect(() => {
    if (otherOrderData?.data) {
      const mapped =
        (otherOrderData.data as OtherOrder[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
      if (mapped.length) {
        setSelectedItems(mapped[0].items ?? []);
        setSelectedLabel(
          mapped[0].order_code ??
            mapped[0].description ??
            `Chứng từ ${mapped[0].id}`,
        );
      } else {
        setSelectedItems([]);
        setSelectedLabel("");
      }
    }
  }, [otherOrderData, setRows]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deleteOtherOrder.isPending));
  }, [isFetching, deleteOtherOrder.isPending, setLoading]);

  const columns = useMemo<ColDef<OtherOrder>[]>(() => {
    return [
      {
        headerName: "Số chứng từ",
        field: "order_code",
        minWidth: 150,
        valueFormatter: (params) =>
          params.value ||
          (params.data?.id ? `CT-${params.data.id}` : "Chứng từ khác"),
      },
      {
        headerName: "Ngày chứng từ",
        field: "order_date",
        minWidth: 150,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDateFilter },
        valueFormatter: (params) => formatDateSafe(params.value),
      },
      {
        headerName: "Mô tả",
        field: "description",
        flex: 1,
        minWidth: 220,
      },
      {
        headerName: "Đối tượng",
        field: "items",
        minWidth: 200,
        valueFormatter: (params) => {
          const first = Array.isArray(params.value) ? params.value[0] : null;
          if (!first) return "";
          return first.object_name || first.object_code || "";
        },
      },
      {
        headerName: "Số dòng",
        field: "items",
        minWidth: 120,
        valueFormatter: (params) =>
          Array.isArray(params.value) ? params.value.length : 0,
      },
      {
        headerName: "Tổng tiền",
        field: "items",
        minWidth: 180,
        valueFormatter: (params) => {
          if (Array.isArray(params.value)) {
            if (!params.value.length) return "";
            const total = params.value.reduce(
              (sum, item) => sum + parseNumber(item.amount),
              0,
            );
            return `${total.toLocaleString("vi-VN")} ₫`;
          }
          if (typeof params.value === "string") return params.value;
          return "";
        },
        valueSetter: (params) => {
          const parsed = parseItems(params.newValue);
          (params.data as OtherOrder).items = parsed;
          return true;
        },
      },
    ];
  }, []);

  const handleDeleteRow = useCallback((row: OtherOrder) => {
    setConfirmRow(row);
  }, []);

  const handleEditRow = useCallback(
    (params: any) => {
      const id = params?.data?.id;
      if (id) {
        navigate(`${ROUTES.ACCOUNTING.OTHER_DOCUMENT_DETAIL}/${id}`);
      }
    },
    [navigate],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteOtherOrder.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa chứng từ khác không thành công");
      },
    });
  }, [confirmRow, deleteOtherOrder, removeRow]);

  const handleRowClick = useCallback((event: any) => {
    const items = sanitizeItems(event?.data?.items);
    setSelectedItems(items);
    setSelectedLabel(
      event?.data?.order_code ??
        event?.data?.description ??
        `Chứng từ ${event?.data?.id ?? ""}`,
    );
  }, []);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          Chứng từ khác
        </h1>
        <p className="text-sm text-gray-600">
          Quản lý các chứng từ khác theo mẫu: ngày chứng từ, mô tả và danh sách
          nghiệp vụ.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.OTHER_DOCUMENT_CREATE)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4 text-green-700" />
              Thêm chứng từ
            </button>
          </div>
        </div>
        <div className="mb-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Chi tiết nghiệp vụ {selectedLabel ? `- ${selectedLabel}` : ""}
            </h3>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Nghiệp vụ
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Mã/Tên đối tượng
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Số tiền
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">VAT</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Giảm VAT
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">PIT</th>
                  <th className="px-3 py-2 text-left font-semibold">Lý do</th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Mô tả
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedItems.length ? (
                  selectedItems.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.operation_name ?? ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {[item.object_code, item.object_name]
                          .filter(Boolean)
                          .join(" - ")}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber(item.amount).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {item.vat_rate !== undefined ? `${item.vat_rate}%` : ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {item.vat_reduce_rate !== undefined
                          ? `${item.vat_reduce_rate}%`
                          : ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {item.pit_rate !== undefined ? `${item.pit_rate}%` : ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.reason ?? ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.description ?? ""}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chọn chứng từ để xem chi tiết nghiệp vụ.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={560}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={otherOrderData?.totalPage}
          onDelete={handleDeleteRow}
          onEdit={handleEditRow}
          onRowClicked={handleRowClick}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách chứng từ, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa chứng từ?"
        description={`Bạn chắc chắn muốn xóa chứng từ "${
          confirmRow?.order_code ?? confirmRow?.id ?? ""
        }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteOtherOrder.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default OtherOrders;
